import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { useHandleInvitations } from '../hooks/useHandleInvitations';

import { Button } from '@/components';
import {
  TourneySearch,
  Sidebar,
  TourneyList,
} from '@/components/GenericsTournaments';
import { InscriptionSearchingResult } from '@/components/GenericsTournaments/SearchingResult';
import { DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useIsLastMatchDay } from '@/hooks/useIsLastMatchDate';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { requestSearchGroups } from '@/services/friendsCup';
import {
  RecommendedListFCup,
  ListItemFCup,
  RequestGroupsSearch,
  ItemResultGroupsResponse,
} from '@/types/friendCup';
import { GenericItemTourneysData } from '@/types/tda';
import { cn, getParticipants } from '@/utils';
import { GENERICS_AMOUNTS, GENERICS_WORDS, TEXTS } from '@/utils/constants';

interface ParamsRequest {
  valueSearch: string;
  page: number;
}

const ITEM_PER_PAGE_MOBILE = 11;
const ITEM_PER_PAGE_DESKTOP = 6;

export const SideBarContent = React.memo(
  ({ list, isData }: { list: RecommendedListFCup; isData: boolean }) => {
    const navigate = useNavigate();
    const { isDesktop, isMobile } = useIsDesktop();

    const { tab, handleTabChanges } = useHandleChangesTabs();
    const { tourneyData, handleSetTourney } = useTourney();
    const [recommended, setRecommended] = useState<
      GenericItemTourneysData[] | null
    >(null);

    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [sendForm, setSendForm] = useState(false);
    const [termSearching, setTermSearching] = useState<string | null>(null);
    const [searchResult, setSearchResult] = useState<
      GenericItemTourneysData[] | null
    >(null);
    const { userInitialData: userLogged } = useAuth();
    const isLastMatchDay = useIsLastMatchDay();
    const [cantidadBusquedas, setCantidadBusquedas] = useState(0);

    const toast = useToast();

    // REQUEST BUSQUEDA GRUPOS
    const {
      mutateAsync: getGroups,
      isLoading,
      data: dataGroups,
      status,
    } = useMutation(
      async (data: RequestGroupsSearch) => await requestSearchGroups(data)
    );

    const { onHandleSubmit } = useHandleInvitations(recommended);

    const { onHandleSubmit: onHandleSubmitSearching } = useHandleInvitations(
      searchResult,
      tab
    );

    // tabs buscador
    const tabs: DefaultTabsProps['tabs'] = useMemo(() => {
      return [{ name: GENERICS_WORDS.teams }, { name: GENERICS_WORDS.coachs }];
    }, []);

    const participants = useCallback((amount: number): string => {
      const rest = amount < GENERICS_AMOUNTS.MAX_PLAYERS_FCUP - 1;
      const participants =
        (rest ? 'Faltan ' : 'Falta ') +
        (GENERICS_AMOUNTS.MAX_PLAYERS_FCUP - amount || 0) +
        ' ' +
        (rest ? GENERICS_WORDS.participants : GENERICS_WORDS.participant);

      return participants;
    }, []);

    const tourneysPending = useMemo(() => {
      return tourneyData.tourneysPending as ListItemFCup[];
    }, [tourneyData.tourneysPending]);

    const tourneysPlaying = useMemo(() => {
      return tourneyData.tourneysPlaying as ListItemFCup[];
    }, [tourneyData.tourneysPlaying]);

    // LISTA DE SUGERIDOS
    const initState = useCallback(() => {
      if (list && list.length > 0) {
        const formatedList = list.map((item: ListItemFCup) => ({
          id: item.id,
          title: item.nombreGrupo,
          disabled:
            Object.values(tourneysPending.filter((obj) => obj.id === item.id))
              .length !== 0 ||
            tourneyData.idsPendings.includes(item.id || 0) ||
            !tourneyData.inscriptionOpen,
          secundaryText: getParticipants(
            item.cantidadDeParticipantes || 0,
            GENERICS_AMOUNTS.MAX_PLAYERS_FCUP
          ),
        }));

        setRecommended(() => {
          return formatedList;
        });
      } else {
        setRecommended(() => {
          return null;
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list, participants]);

    // INIT STATE = SUGERIDOS
    useEffect(() => {
      initState();

      return () => {
        setRecommended(() => {
          return null;
        });
      };
    }, [initState]);

    // TODO: ordenar con mayor cantidad de participantes
    const sortGroups = (tournaments: ItemResultGroupsResponse[]) => {
      return tournaments.sort(
        (a: ItemResultGroupsResponse, b: ItemResultGroupsResponse) => {
          return a.nombreGrupo.localeCompare(b.nombreGrupo);
        }
      );
    };

    const itemSearchIsPlaying = useCallback(
      (id: number): ListItemFCup | undefined => {
        if (tourneysPlaying && tourneysPlaying.length > 0) {
          return (
            tourneysPlaying && tourneysPlaying.find((obj) => obj.id === id)
          );
        }
      },
      [tourneysPlaying]
    );

    // CREO LISTA GENERICA DE BUSQUEDA DE GRUPO
    const createListItemsGroup = useCallback(
      (listResponse: ItemResultGroupsResponse[]) => {
        const sortedGroups = sortGroups(listResponse);
        const list = sortedGroups.reduce<GenericItemTourneysData[]>(
          (acc, item) => {
            const txtPlaying = itemSearchIsPlaying(item.id)
              ? 'Participando'
              : '';

            acc.push({
              id: item.id,
              action: {
                txtAction: 'Enviar solicitud',
                txtActionDisabled: txtPlaying || 'Esperando aceptación',
                variantButton: 'secondary',
              },
              title: item.nombreGrupo,
              subtitle: `${item.nombreCreadorGrupo} ${item.apellidoCreadorGrupo}`,
              disabled:
                tourneysPending.filter((obj) => obj.id === item.id).length !==
                  0 ||
                tourneyData.idsPendings.includes(item.id) ||
                Boolean(txtPlaying),
              secundaryText: getParticipants(
                item.cantidadDeParticipantes,
                GENERICS_AMOUNTS.MAX_PLAYERS_FCUP
              ),
            });

            return acc;
          },
          []
        );

        return list;
      },
      [itemSearchIsPlaying, tourneysPending, tourneyData.idsPendings]
    );

    // seteo lista de items genericos por grupo
    const setGenericListGroups = useCallback(
      (list: GenericItemTourneysData[]) => {
        setSearchResult([]);
        handleTabChanges(TabType.FirstTab);
        setSearchResult(list);
      },
      [handleTabChanges]
    );

    const sortDts = (dtsList: ItemResultGroupsResponse[]) => {
      // Count frequency of idUsuarioOwner
      const ownerFrequency = dtsList.reduce(
        (acc: any, dts: ItemResultGroupsResponse) => {
          acc[dts.id] = (acc[dts.id] || 0) + 1;
          return acc;
        },
        {}
      );

      return dtsList.sort((a, b) => {
        const freqDiff = ownerFrequency[b.id] - ownerFrequency[a.id];
        if (freqDiff !== 0) return freqDiff;

        return a.nombreGrupo.localeCompare(b.nombreGrupo);
      });
    };

    const createListItemsDts = useCallback(
      (listResponse: ItemResultGroupsResponse[]) => {
        // Sort the tournaments
        const sortedDts = sortDts(listResponse);
        const list = sortedDts.reduce<GenericItemTourneysData[]>(
          (acc, item) => {
            const txtPlaying = itemSearchIsPlaying(item.id || 0)
              ? 'Participando'
              : '';

            acc.push({
              id: item.id,
              action: {
                txtAction: 'Enviar solicitud',
                txtActionDisabled: txtPlaying || 'Esperando aceptación',
                variantButton: 'secondary',
              },
              disabled:
                Object.values(
                  tourneysPending.filter((obj) => obj.id === item.id)
                ).length !== 0 ||
                tourneyData.idsPendings.includes(item.id) ||
                Boolean(txtPlaying),
              title: `${item.nombreCreadorGrupo} ${item.apellidoCreadorGrupo}`,
              subtitle: isMobile
                ? item.nombreGrupo.toUpperCase()
                : item.nombreGrupo,
              secundaryText: item.provincia,
            });

            return acc;
          },
          []
        );
        return list;
      },
      [isMobile, itemSearchIsPlaying, tourneyData.idsPendings, tourneysPending]
    );

    // seteo lista de items genericos segun servicio de busqueda
    const setGenericListDts = useCallback(
      (list: GenericItemTourneysData[]) => {
        setSearchResult([]);
        handleTabChanges(TabType.SecondTab);
        setSearchResult(list);
      },
      [handleTabChanges]
    );

    const setTableGroupsResult = useCallback(
      (page: number, list: ItemResultGroupsResponse[]) => {
        if (tab === TabType.FirstTab) {
          const listItemsGroups = createListItemsGroup(list);
          if (page === 1) {
            setGenericListGroups(listItemsGroups);
          } else {
            const newPage = listItemsGroups;
            searchResult && setGenericListGroups([...searchResult, ...newPage]);
          }
        } else {
          const listItemsDTs = createListItemsDts(list);
          if (page === 1) {
            setGenericListDts(listItemsDTs);
          } else {
            const newPage = listItemsDTs;
            searchResult && setGenericListDts([...searchResult, ...newPage]);
          }
        }
      },
      [
        createListItemsDts,
        createListItemsGroup,
        searchResult,
        setGenericListDts,
        setGenericListGroups,
        tab,
      ]
    );

    const handleSetGenericsByTabs = useCallback(
      (tab: TabType, data: ItemResultGroupsResponse[]) => {
        if (tab === TabType.FirstTab) {
          setGenericListGroups(createListItemsGroup(data));
        } else {
          setGenericListDts(createListItemsDts(data));
        }
      },
      [
        setGenericListGroups,
        createListItemsGroup,
        setGenericListDts,
        createListItemsDts,
      ]
    );

    const handleFilterByTab = useCallback(
      async (tab: TabType) => {
        tab && handleTabChanges(tab);
        setSendForm(true);
        if (dataGroups && status && dataGroups.page) {
          handleSetGenericsByTabs(tab, dataGroups.page?.objectList);
        }
      },
      [handleTabChanges, dataGroups, status, handleSetGenericsByTabs]
    );

    // manejo de envio de consulta para grupos
    const onHandleRequest = useCallback(
      async ({ valueSearch, page }: ParamsRequest) => {
        try {
          setTermSearching(valueSearch);
          const response = await getGroups({
            paginaActual: page,
            cantPorPagina: isMobile
              ? ITEM_PER_PAGE_MOBILE
              : ITEM_PER_PAGE_DESKTOP,
            nombreCreador: '',
            nombreGrupo: tab === TabType.FirstTab ? valueSearch : '',
            apellidoCreador: tab === TabType.SecondTab ? valueSearch : '',
          });

          if (response.estado && response?.page) {
            setTableGroupsResult(page, response?.page?.objectList);
            setHasMore(Boolean(response.page?.hasNext));
            setCurrentPage(page);
            setCantidadBusquedas((prev) => prev + 1);
          } else {
            toast.show(TEXTS.defaultError);
          }
        } catch (error) {
          console.error(TEXTS.defaultError);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [getGroups, tab, toast, isMobile]
    );

    const loadMore = useCallback(
      async (pageNumber?: number) => {
        if (isLoading || !dataGroups) return;
        const nextPage = pageNumber || currentPage + 1;
        termSearching &&
          (await onHandleRequest({
            valueSearch: termSearching,
            page: nextPage,
          }));
      },
      [isLoading, dataGroups, currentPage, onHandleRequest, termSearching]
    );

    const onHandleRefreshAfterInvitation = useCallback(
      (ids: number[], isPendings = true) => {
        const numberSet = new Set(ids);

        const disabledParticipant = {
          action: {
            txtAction: 'Enviar solicitud',
            txtActionDisabled: 'Participando',
          },
        };

        setSearchResult((prevSearchResult) => {
          if (!prevSearchResult) return null;
          return prevSearchResult.map((item) => ({
            ...item,
            disabled: (item.id && numberSet.has(item.id)) || item.disabled,
            ...(!isPendings && disabledParticipant),
          }));
        });

        setRecommended((prevRecommend) => {
          if (!prevRecommend) return null;
          return prevRecommend.map((item) => ({
            ...item,
            disabled: (item.id && numberSet.has(item.id)) || item.disabled,
            ...(!isPendings && disabledParticipant),
          }));
        });
      },
      []
    );

    useEffect(() => {
      if (tourneyData.idsPendings.length > 0) {
        onHandleRefreshAfterInvitation(tourneyData.idsPendings, true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tourneyData.idsPendings]);

    useEffect(() => {
      if (tourneyData.idsPlayings.length > 0) {
        onHandleRefreshAfterInvitation(tourneyData.idsPlayings, false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tourneyData.idsPlayings]);

    return (
      <Sidebar>
        {isDesktop && (
          <Button
            className="w-full"
            disabled={isLastMatchDay || !tourneyData.inscriptionOpen}
            type="button"
            variant="primary"
            onClick={() => {
              if (userLogged?.dtExtranjero) {
                return toast.show(TEXTS.exclusiveForArgentinianDTs);
              }
              navigate(ROUTES.friendsCup.createTeam);
            }}
          >
            Crear Equipo
          </Button>
        )}

        {/* Buscador grupos existentes */}
        <TourneySearch
          autocomplete={false}
          className="mt-6 lg:mt-11"
          disabled={isLastMatchDay || !tourneyData.inscriptionOpen}
          label="Inscribite a un equipo existente"
          placeholder="Nombre del equipo o administrador"
          sendForm={sendForm}
          tabSelected={tab}
          onHandleCleanResult={() => {
            setSearchResult(null);
            setCantidadBusquedas(0);
          }}
          onHandleRequest={onHandleRequest}
          onHandleTabSelected={handleTabChanges}
        />

        {searchResult && (
          <div className={cn({ 'min-h-48': searchResult })}>
            <InscriptionSearchingResult
              cantidadBusquedas={cantidadBusquedas}
              hasMore={hasMore}
              list={searchResult}
              loadMore={loadMore}
              tabSelected={tab}
              tabs={tabs}
              onHandleSubmit={onHandleSubmitSearching}
              onSubmitTabSelected={handleFilterByTab}
            />
          </div>
        )}

        {/* Torneos Recomendados */}
        {recommended && (
          <TourneyList
            action={{
              txtAction: 'Enviar solicitud',
              txtActionDisabled: isLastMatchDay
                ? 'Enviar solicitud'
                : !tourneyData.inscriptionOpen
                ? 'Enviar solicitud'
                : 'Esperando aceptación',
              variantButton: 'secondary',
            }}
            className="mt-4"
            handleSubmit={onHandleSubmit}
            list={recommended || []}
            title="Recomendados"
          />
        )}
        {/* TODO: Mostrar con datos y desktop */}
        {(isMobile || (isDesktop && isData)) && (
          <div className="text-center">
            <Button
              className="mt-8 mx-auto font-bold"
              disabled={
                (isLastMatchDay && !userLogged?.veda) ||
                (!tourneyData.inscriptionOpen && !userLogged?.veda)
              }
              type="button"
              variant="text"
              onClick={() => {
                isMobile
                  ? navigate(ROUTES.friendsCup.info)
                  : handleSetTourney({ ...tourneyData, showModalInfo: true });
              }}
            >
              Más información
            </Button>
          </div>
        )}
      </Sidebar>
    );
  }
);

SideBarContent.displayName = 'SideBarContent';
